import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { getDiagnosticsEmailConfig, sendDiagnosticsTestEmail } from '../lib/api';
import { toast } from 'sonner';
import { Mail, Send, CheckCircle2, XCircle, Loader2, Link2, AtSign } from 'lucide-react';

const TEMPLATE_LABELS = {
  all: 'Todos (enviar 7 correos en secuencia)',
  welcome: 'Bienvenida',
  password_reset: 'Restablecer contraseña',
  port_arrival: 'Arribo a Puerto',
  pantaco_arrival: 'Arribo a Pantaco',
  destino_arrival: 'Salida de Pantaco (En Destino)',
  delivered: 'Entregado',
  account_locked: 'Cuenta bloqueada',
};

export function EmailDiagnosticsCard() {
  const [config, setConfig] = useState(null);
  const [to, setTo] = useState('');
  const [template, setTemplate] = useState('all');
  const [sending, setSending] = useState(false);
  const [lastResults, setLastResults] = useState(null);

  useEffect(() => {
    getDiagnosticsEmailConfig()
      .then((r) => setConfig(r.data))
      .catch(() => {});
  }, []);

  const handleSend = async () => {
    setSending(true);
    setLastResults(null);
    try {
      const payload = { template: template === 'all' ? null : template };
      if (to) payload.to = to;
      const res = await sendDiagnosticsTestEmail(payload);
      setLastResults(res.data);
      const errors = (res.data.results || []).filter((r) => r.status !== 'sent');
      if (errors.length === 0) {
        toast.success(`Correos enviados correctamente a ${res.data.to}`);
      } else {
        toast.warning(`${res.data.results.length - errors.length}/${res.data.results.length} enviados. Revisa los errores.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al enviar correo de prueba');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 space-y-4" data-testid="email-diagnostics-card">
      <div className="flex items-center gap-2">
        <Mail size={20} />
        <div>
          <h2 className="text-xl font-heading">Diagnóstico de correos</h2>
          <p className="text-sm text-muted-foreground">
            Verifica que las notificaciones lleguen bien (plantillas, remitente, links).
          </p>
        </div>
      </div>

      {/* Config summary */}
      {config && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
            <div className="flex items-start gap-2 p-3 bg-muted/40 rounded border border-border/50">
              <AtSign size={14} className="mt-0.5 text-muted-foreground" />
              <div>
                <div className="text-xs text-muted-foreground">Remitente configurado</div>
                <div className="font-mono text-xs" data-testid="email-diag-sender">{config.sender_email}</div>
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 bg-muted/40 rounded border border-border/50">
              <Link2 size={14} className="mt-0.5 text-muted-foreground" />
              <div>
                <div className="text-xs text-muted-foreground">Links apuntan a</div>
                <div className="font-mono text-xs" data-testid="email-diag-url">{config.link_base_url}</div>
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 bg-muted/40 rounded border border-border/50">
              {config.resend_configured ? (
                <CheckCircle2 size={14} className="mt-0.5 text-emerald-500" />
              ) : (
                <XCircle size={14} className="mt-0.5 text-red-500" />
              )}
              <div>
                <div className="text-xs text-muted-foreground">Resend API</div>
                <div className="text-xs" data-testid="email-diag-resend">
                  {config.resend_configured ? 'Configurado' : 'NO configurado'}
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 bg-muted/40 rounded border border-border/50">
              <AtSign size={14} className="mt-0.5 text-muted-foreground" />
              <div>
                <div className="text-xs text-muted-foreground">Huella API key</div>
                <div className="font-mono text-xs" data-testid="email-diag-fingerprint">{config.resend_api_key_fingerprint || '—'}</div>
              </div>
            </div>
          </div>

          {/* Domain verification hint: if sender_email uses your custom domain but
              the actual delivered email shows `onboarding@resend.dev`, the domain
              is not verified in Resend yet. */}
          {config.sender_email && !config.sender_email.includes('resend.dev') && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-sm p-3 text-xs" data-testid="email-diag-domain-hint">
              <p className="text-blue-300 font-medium mb-1">¿Recibes correos desde <code className="font-mono">onboarding@resend.dev</code>?</p>
              <p className="text-muted-foreground">
                Significa que tu dominio <code className="font-mono">{config.sender_email.split('@')[1]}</code> aún <strong>no está verificado en Resend</strong>.
                Ve a <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">resend.com/domains</a>,
                agrega el dominio y copia los registros DNS (SPF + DKIM) al panel de tu proveedor de dominio (Cloudflare/GoDaddy/etc).
                Tarda 5-30 min en propagar. Una vez verde, los correos saldrán desde el remitente configurado.
              </p>
            </div>
          )}
        </>
      )}

      {/* Send test form */}
      <div className="grid grid-cols-1 md:grid-cols-[2fr,2fr,auto] gap-3 items-end">
        <div>
          <Label className="text-xs">Correo destino (vacío = tu correo)</Label>
          <Input
            type="email"
            placeholder="opcional — tu correo por default"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            data-testid="email-diag-to"
          />
        </div>
        <div>
          <Label className="text-xs">Plantilla</Label>
          <Select value={template} onValueChange={setTemplate}>
            <SelectTrigger data-testid="email-diag-template"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(TEMPLATE_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={handleSend} disabled={sending} data-testid="email-diag-send-btn">
          {sending ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />}
          Enviar prueba
        </Button>
      </div>

      {/* Results */}
      {lastResults && (
        <div className="border border-border/50 rounded p-3 bg-muted/20">
          <div className="text-sm font-medium mb-2">
            Resultado · destino: <span className="font-mono">{lastResults.to}</span>
          </div>
          <div className="space-y-1 text-sm">
            {(lastResults.results || []).map((r, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`email-diag-result-${r.template}`}>
                {r.status === 'sent' ? (
                  <CheckCircle2 size={14} className="text-emerald-500" />
                ) : (
                  <XCircle size={14} className="text-red-500" />
                )}
                <span className="font-mono text-xs">{TEMPLATE_LABELS[r.template] || r.template}</span>
                {r.error && <span className="text-xs text-red-400">— {r.error}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
